import moment from 'moment'

import { AuditAction } from 'types/api.graphql'
import { AuditLogLike, KnockMessageLogLike } from 'types/vagueTypes'

const TIME_WINDOW_MINS = 5

/**
 * This helper tries to smoosh multiple similar audit logs down into a single log. The main thing it is covering is audit log spam on a record where
 * auto-save is present. My dream is that one day the API will do a lot of this consolidation and smarts and we can simply just render what the API
 * gives us. If this function is causing problems and you CBF trying to improve it, just delete it and let the excessive audit logs show.
 */
export const consolidateAuditLogs = (auditLogs: AuditLogLike[]) => {
  const consolidatedLogs = auditLogs.reduce<AuditLogLike[]>((acc, curr) => {
    if (curr.action === AuditAction.Message) {
      return [...acc, curr] // message logs operate slightly differently with only a newSnapshot so don't consolidate them
    }

    const prevLog = acc[acc.length - 1]

    if (!prevLog) return [curr]

    const sameAction = curr.action === prevLog.action
    const sameUser = curr.actionByIdentity === prevLog.actionByIdentity

    // bail out with the cheapest comparison
    if (sameAction && sameUser) {
      // check if the logs are within a time window from each other
      const previousDate = moment(prevLog.createdAt)
      const currentDate = moment(curr.createdAt)

      const timeDiff = currentDate.diff(previousDate, 'minutes')
      const withinTimeWindow = timeDiff >= 0 && timeDiff <= TIME_WINDOW_MINS

      if (withinTimeWindow) {
        const prevSnapshot = prevLog.newSnapshot ?? {}
        const currSnapshot = curr.newSnapshot ?? {}

        const keys = Object.keys(prevSnapshot)

        // attempt to check any status fields because changing this is usually important
        const statusesTheSame = keys
          .filter((key) => key.toLowerCase().endsWith('status'))
          .every((key) => prevSnapshot[key] === currSnapshot[key])

        if (statusesTheSame) {
          // if any date fields have changed, we likely don't want to merge
          const dateKeysSame = keys
            .filter((key) => key.endsWith('At') && key !== 'updatedAt')
            .every((key) => prevSnapshot[key] === currSnapshot[key])

          if (dateKeysSame) {
            return [
              ...acc.slice(0, -1),
              { ...prevLog, newSnapshot: currSnapshot },
            ]
          }
        }
      }
    }

    return [...acc, curr]
  }, [])

  return consolidatedLogs
}

type ConsolidateMessageLog = Omit<KnockMessageLogLike, 'status'> & {
  status: string
}

export const consolidateMessageLogs = (messages: KnockMessageLogLike[]) => {
  const consolidatedLogs = messages.reduce<ConsolidateMessageLog[]>(
    (acc, curr) => {
      const prevLogIndex = acc.findLastIndex(({ messageId, sentTo }) => {
        return messageId === curr.messageId && sentTo?.id === curr.sentTo?.id
      })

      const prevLog = acc[prevLogIndex]

      if (!prevLog) {
        return [...acc, curr]
      }

      // check if the logs are within a time window from each other
      const previousDate = moment(prevLog.logDate)
      const currentDate = moment(curr.logDate)

      const timeDiff = currentDate.diff(previousDate, 'minutes')
      const withinTimeWindow = timeDiff >= 0 && timeDiff <= TIME_WINDOW_MINS

      if (withinTimeWindow) {
        const joinedStatus = `${prevLog.status} and ${curr.status}`

        return [
          ...acc.slice(0, prevLogIndex),
          { ...prevLog, status: joinedStatus, logDate: curr.logDate },
          ...acc.slice(prevLogIndex + 1),
        ]
      }

      return [...acc, curr]
    },
    []
  )

  return consolidatedLogs
}
